import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { RECORDS_PER_PAGE_OPTIONS } from '../../../../utils/constants';

import ProductClassificationModalContainer from '../../../../containers/admin/product-specifications/product-classification-modal/ProductClassificationModal.container';
import ProductCategoryModalContainer from '../../../../containers/admin/product-specifications/product-category-modal/ProductCategoryModal.container';
import ProductTypeModalContainer from '../../../../containers/admin/product-specifications/product-type-modal/ProductTypeModal.container';
import Dropdown from '../../../common/inputs/dropdown/Dropdown';
import Button from '../../../common/button/Button';
import Table from '../../../common/table/Table';

import './product-specifications.scss';

const ProductSpecifications = ({
  specifications,
  currentPage,
  totalPages,
  retrieveClassifications,
  retrieveCategories,
  retrieveTypes,
  retrieveSpecifications,
  specification,
  navigateToProductSpecifications,
}) => {
  const { classifications, categories, types } = useSelector(({ product }) => product);

  const [recordsPerPage, setRecordsPerPage] = useState({ label: 20, value: 20 });
  const [productClassification, setProductClassification] = useState(undefined);
  const [productCategory, setProductCategory] = useState(undefined);
  const [productType, setProductType] = useState(undefined);
  const [showClassificationModal, setShowClassificationModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [selectedSpecification, setSelectedSpecification] = useState(undefined);

  const filters = useRef({
    recordsPerPage: 20,
    page: 0,
  });

  useEffect(() => {
    if (specification && specifications) {
      const filteredProduct = specifications.find((specificationFromArray) => {
        return specificationFromArray.specification.type.externalReference === specification;
      });

      if (filteredProduct) {
        handleRowClick(filteredProduct);
      } else {
        setShowTypeModal(false);
      }
    }
  }, [specification, specifications]);

  const handleClassificationChange = (classification) => {
    setProductClassification(classification);
    setProductCategory(null);
    setProductType(null);
    retrieveCategories(classification.externalReference);
  };

  const handleCategoryChange = (category) => {
    setProductCategory(category);
    setProductType(null);
    retrieveTypes(category.externalReference);
  };

  const handleTypeChange = (type) => {
    setProductType(type);
  };

  const handleRowClick = (row) => {
    setSelectedSpecification({ ...row.specification });
    setShowTypeModal(true);
  };

  const haveFiltersChanged = () => {
    let definedKeys = 0;
    for (let key in filters.current) {
      if (filters.current[key] !== undefined && filters.current[key] !== null && filters.current[key] !== '') {
        definedKeys++;
      }
    }

    if (filters.current.page !== 0 || filters.current.recordsPerPage !== 20 || definedKeys !== 2) {
      return true;
    }

    return false;
  };

  const applyFilters = (page) => {
    filters.current = {
      classificationExternalReference: productClassification?.externalReference,
      categoryExternalReference: productCategory?.externalReference,
      typeExternalReference: productType?.externalReference,
      recordsPerPage: recordsPerPage.value,
      page,
    };

    retrieveSpecifications(filters.current);
  };

  const removeFilters = () => {
    filters.current = {
      recordsPerPage: 20,
      page: 0,
    };

    setRecordsPerPage(RECORDS_PER_PAGE_OPTIONS[1]);
    setProductClassification(undefined);
    setProductCategory(undefined);
    setProductType(undefined);

    retrieveSpecifications(filters.current);
  };

  return (
    <section className="product-specifications" data-testid="product-specifications">
      <section className="product-specifications__filter" data-testid="product-specifications-filters">
        <section className="product-specifications__filter__filters">
          <p>Filter</p>

          <label htmlFor="records-per-page">Records per Page</label>
          <Dropdown
            name="records-per-page"
            value={recordsPerPage}
            options={RECORDS_PER_PAGE_OPTIONS}
            onChange={(option) => setRecordsPerPage(option)}
          />

          <label htmlFor="product-classification">Product Classification</label>
          <Dropdown
            name="product-classification"
            value={productClassification}
            options={classifications}
            onChange={handleClassificationChange}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.externalReference}
          />

          <label htmlFor="product-category">Product Category</label>
          <Dropdown
            name="product-category"
            value={productCategory}
            options={categories}
            onChange={handleCategoryChange}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.externalReference}
            disabled={!productClassification}
          />

          <label htmlFor="product-type">Product Type</label>
          <Dropdown
            name="product-type"
            value={productType}
            options={types}
            onChange={handleTypeChange}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.externalReference}
            disabled={!productCategory}
          />
        </section>

        <div className="product-specifications__filter__button-container">
          <Button onClick={() => applyFilters(0)}>Apply Filter</Button>
          {haveFiltersChanged() && <Button onClick={() => removeFilters()}>Remove Filter</Button>}
        </div>
      </section>

      <section className="product-specifications__table">
        <section className="product-specifications__table__header">
          <h1>Product Specifications</h1>
          <div className="product-specifications__table__header__buttons">
            <Button onClick={() => setShowClassificationModal(true)}>Manage Classifications</Button>
            <Button onClick={() => setShowCategoryModal(true)}>Manage Categories</Button>
            <Button onClick={() => setShowTypeModal(true)}>Add Type</Button>
          </div>
        </section>

        <section className="product-specifications__table__container">
          <Table
            data={specifications}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={applyFilters}
            onRowClick={handleRowClick}
            emptyMessage="No product specifications found"
            hiddenColumns={['specification']}
          />
        </section>
      </section>

      <ProductClassificationModalContainer
        show={showClassificationModal}
        setShow={setShowClassificationModal}
        filters={filters.current}
        retrieveSpecifications={retrieveSpecifications}
        retrieveClassifications={retrieveClassifications}
      />
      <ProductCategoryModalContainer
        show={showCategoryModal}
        setShow={setShowCategoryModal}
        filters={filters.current}
        retrieveSpecifications={retrieveSpecifications}
        retrieveCategories={retrieveCategories}
      />
      <ProductTypeModalContainer
        show={showTypeModal}
        setShow={setShowTypeModal}
        filters={filters.current}
        specification={selectedSpecification}
        retrieveSpecifications={retrieveSpecifications}
        retrieveCategories={retrieveCategories}
        retrieveTypes={retrieveTypes}
        onHide={() => {
          if (specification) {
            navigateToProductSpecifications();
          }
          setSelectedSpecification(undefined);
        }}
      />
    </section>
  );
};

ProductSpecifications.propTypes = {
  specifications: PropTypes.arrayOf(PropTypes.shape({})),
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  retrieveClassifications: PropTypes.func.isRequired,
  retrieveCategories: PropTypes.func.isRequired,
  retrieveTypes: PropTypes.func.isRequired,
  retrieveSpecifications: PropTypes.func.isRequired,
  specification: PropTypes.string,
  navigateToProductSpecifications: PropTypes.func.isRequired,
};

ProductSpecifications.defaultProps = {
  specifcations: [],
  specification: undefined,
};

export default ProductSpecifications;
